<template>
  <div class="col px-0">
    <v-overlay :value="loading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <div class="text-subtitle-1 text-sm-h6 text-md-h5 font-weight-bold text-center">
      Relatório de Movimentação de Estoque
    </div>

    <v-card>  
      <v-card-title>
        <span>Filtros</span>
      </v-card-title>
      <v-card-text>
        <v-row>

          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="itemIds"
              label="Item(s) de Estoque"
              :items="filtersList ? filtersList.accountContext.allItemsFromAccountStorages : []"
              item-value="id"
              item-text="name"
              clearable
              @click:clear="itemIds = []"
              :loading="loadingFilters"
              multiple
              chips
              dense
              deletable-chips
              hint="Pelo menos 1 item deve ser selecionado"
              persistent-hint
              :rules="[this.requiredRule, this.minArrayRequiredRule]"
            />
          </v-col>

          <v-col cols="12" md="6" >
            <v-autocomplete
              v-model="storageIds"
              label="Estoque(s)"
              :items="filtersList ? filtersList.accountContext.allStorages : []"
              item-value="id"
              item-text="name"
              clearable
              @click:clear="storageIds = []"
              :loading="loadingFilters"
              multiple
              chips
              dense
              deletable-chips
              hint="Se nenhum estoque for selecionado, todos serão exibidos"
              persistent-hint
            />
          </v-col>

          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="packagingIds"
              label="Embalagem(s)"
              :items="filtersList ? filtersList.accountContext.allPackagingsFromAccountStorages : []"
              item-value="id"
              item-text="name"
              clearable
              @click:clear="packagingIds = []"
              :loading="loadingFilters"
              multiple
              chips
              dense
              deletable-chips
              hint="Se nenhuma embalagem for selecionada, todas serão exibidas"
              persistent-hint
            />
          </v-col>

          <v-col cols="6" md="3">
            <v-autocomplete
              v-model="periodFilterModel"
              label="Período"
              :items="periodFilterOptions"
              item-value="key"
              item-text="name"
            />
          </v-col>
          <v-col cols="12" md="4" v-if="periodFilterModel == 'OTHER'">
            <DateRangeField
              :inputData.sync="periodFilterRangeModel"
              label="Período Customizado"
              :hasTodayDate="true"
            />
          </v-col>

          <v-col cols="6" md="2">
            <v-btn
              class="mt-3"
              @click="runFilter(false)"
              :disabled="itemIds.length < 1"
            >
              <v-icon left>mdi-magnify</v-icon>
              Filtrar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <div>
      <BasePrint
        printableElementId="storageMovementReportPrintTable"
        printButtonLabel="Imprimir"
        printTitle="Relatório de Movimentação de Estoque"
        @start-printing="printing = loading = true"
        @stop-printing="printing = loading = false"
        class="mt-3"
      />
    </div>

    <div id="storageMovementReportPrintTable" v-if="storageMovementsReport" class="mt-5">

      <v-row v-if="storageMovementsReport.length < 1">
        <v-col cols="12">
          <p class="text-center">
            Nenhum resultado encontrado.
          </p>
        </v-col>
      </v-row>

      <v-row v-else v-for="storageMovement in storageMovementsReport" v-bind:key="storageMovement.itemCode + storageMovement.packaging">
        <v-col cols="12">
          <v-card
            elevation="2"
            outlined
            class="pa-3"
          >
            <v-row>
              <v-col cols="12" md="3" lg="2">
                <v-card outlined shaped>
                  <v-card-title class="py-2">
                    <span class="text-h6">Código</span>
                  </v-card-title>

                  <v-card-text class="text-right">
                    <span class="font-weight-bold text-subtitle-1 text-sm-h6">
                      {{storageMovement.itemCode}}
                    </span>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="9" lg="6">
                <v-card outlined shaped>
                  <v-card-title class="py-2">
                    <span class="text-h6">Item</span>
                  </v-card-title>

                  <v-card-text class="text-right">
                    <span class="font-weight-bold text-subtitle-1 text-sm-h6">
                      {{storageMovement.item}}
                    </span>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="12" lg="4">
                <v-card outlined shaped>
                  <v-card-title class="py-2">
                    <span class="text-h6">Embalagem</span>
                  </v-card-title>

                  <v-card-text class="text-right">
                    <span class="font-weight-bold text-subtitle-1 text-sm-h6">
                      {{storageMovement.packaging}}
                    </span>
                  </v-card-text>
                </v-card>
              </v-col>


              <v-col cols="12" md="12" v-for="storage in storageMovement.storages" v-bind:key="storage.name + storageMovement.itemCode + storageMovement.packaging">
                <v-card outlined shaped>
                  <v-card-title class="py-2">
                    <span class="text-h6">Movimentações no Estoque</span>
                  </v-card-title>

                  <v-card-text class="text-right">
                    <span class="font-weight-bold text-subtitle-1 text-sm-h6">
                      {{storage.name}}
                    </span>
                  </v-card-text>
                </v-card>

                <BaseIndex
                  ref="baseIndex"
                  v-if="storageMovementsReport"
                  :loading="loading"
                  :pageLoading="pageLoading"
                  :table-headers="tableHeaders"
                  :table-items="storage.movements"
                  :maskFields="maskFields"
                  :itemClass="itemClasses"
                  :enableCustomFilters="false"
                  :containTopToolbar="false"
                  :itemsPerPage="-1"
                  :disablePagination="true"
                  :indexNested="true"
                  noDataText="Nenhuma movimentação deste item neste estoque de acordo com as datas informadas"
                  :hasDefaultPageTitle="false"
                >
                  <template slot="tableHeader">
                    <tr class="d-none d-md-table-row">
                      <td colspan="1" class="text-right title"><strong class="mx-3">Data Inicial:</strong></td>
                      <td class="text-left title"><strong>{{ storage.initialDate | formatDate }}</strong></td>
                      <td colspan="3" class="text-right title"><strong class="mx-3">Estoque Inicial:</strong></td>
                      <td colspan="1" class="text-left title"><strong>{{ storage.initialQuantity | formatNumber }}</strong></td>
                    </tr>

                    <!-- for mobile only -->
                    <tr class="d-table-row d-md-none v-data-table__mobile-table-row">
                      <td class="v-data-table__mobile-row">
                        <div class="v-data-table__mobile-row__header">Data Inicial</div>
                        <div class="v-data-table__mobile-row__cell"><strong>{{ storage.initialDate | formatDate }}</strong></div>
                      </td>
                      <td class="v-data-table__mobile-row">
                        <div class="v-data-table__mobile-row__header">Estoque Inicial</div>
                        <div class="v-data-table__mobile-row__cell"><strong>{{ storage.initialQuantity | formatNumber }}</strong></div>
                      </td>
                    </tr>
                  </template>

                  <template slot="tableFooter">
                    <tr class="d-none d-md-table-row">
                      <td colspan="1" class="text-right title"><strong class="mx-3">Data Final:</strong></td>
                      <td class="text-left title"><strong>{{ storage.endDate | formatDate }}</strong></td>
                      <td colspan="3" class="text-right title"><strong class="mx-3">Estoque Final:</strong></td>
                      <td colspan="1" class="text-left title"><strong>{{ storage.storageFinalBalance | formatNumber }}</strong></td>
                    </tr>

                    <!-- for mobile only -->
                    <tr class="d-table-row d-md-none v-data-table__mobile-table-row">
                      <td class="v-data-table__mobile-row">
                        <div class="v-data-table__mobile-row__header">Data Final</div>
                        <div class="v-data-table__mobile-row__cell"><strong>{{ storage.endDate | formatDate }}</strong></div>
                      </td>
                      <td class="v-data-table__mobile-row">
                        <div class="v-data-table__mobile-row__header">Estoque Final</div>
                        <div class="v-data-table__mobile-row__cell"><strong>{{ storage.storageFinalBalance | formatNumber }}</strong></div>
                      </td>
                    </tr>
                  </template>

                </BaseIndex>
              </v-col>

              <v-col cols="12" md="12">
                <v-card outlined shaped>
                  <v-card-title class="py-2">
                    <span class="text-h6">Estoque Final Total</span>
                  </v-card-title>

                  <v-card-text class="text-right">
                    <span class="font-weight-bold text-subtitle-1 text-sm-h6">
                      {{storageMovement.storagesBalance | formatNumber }}
                    </span>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card>
        <!-- <v-divider></v-divider> -->
        </v-col>
      </v-row>

      <!-- <v-row v-if="storageMovementsReport">
        <v-col cols="12">
          <v-card>
            <v-card-title class="py-2">
              <span class="text-h6">Valor Total</span>
            </v-card-title>

            <v-card-text class="text-right">
              <span class="font-weight-bold text-h6">
                {{salesByProductAnalyticReport.totalValue | formatCurrency}}
              </span>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row> -->

    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import moment from "moment";
import BaseIndex from "@shared_components/base_index.vue";
import DateRangeField from "@shared_components/date_range_field.vue"
import BasePrint from "@shared_components/base_print.vue";

export default {
  data() {
    return {
      loading: false,
      loadingFilters: false,
      pageLoading: false,
      monthFilter: moment().format('YYYY-MM'),
      storageIds: [],
      itemIds: [],
      packagingIds: [],
      storageMovementsReport: null,
      startDate: null,
      endDate: null,
      filtersList: null,
      tableHeaders: [
        { text: "Data", align: "left", sortable: false, value: "date"},
        { text: "Motivo", align: "left", sortable: false, value: "targetTypeName"},
        { text: "Origem/Destino", align: "left", sortable: false, value: "targetName"},
        { text: "Qtd. Entrada", align: "left", sortable: false, value: "entryQuantity"},
        { text: "Qtd. Saída", align: "left", sortable: false, value: "exitQuantity"},
        { text: "Saldo", align: "left", sortable: false, value: "balance"},
      ],
      maskFields: [
        { field: 'entryQuantity', mask: 'number' },
        { field: 'exitQuantity', mask: 'number' },
        { field: 'balance', mask: 'number' },
        { field: 'date', mask: 'date' },
        // { field: 'itemTotalPrice', mask: 'currency' },
        // { field: 'status', mask: 'status_color', colorField: 'statusColor' },
      ],
      periodFilterOptions: [
        { "key": null, "name": "Sem filtro de data" },
        { "key": "DAY", "name": "Hoje" },
        { "key": "WEEK", "name": "Esta Semana" },
        { "key": "MONTH", "name": "Este Mês" },
        { "key": "OTHER", "name": "Customizado" },
      ],
      periodFilterModel: "MONTH",
      periodFilterRangeModel: [moment().startOf('month').format('YYYY-MM-DD'), moment().endOf('month').format('YYYY-MM-DD')]
    };
  },
  props: {
  },
  components: {
    BaseIndex: BaseIndex,
    DateRangeField: DateRangeField,
    BasePrint: BasePrint,
  },
  created() {
    this.loadServerFilters();
  },
  mounted() {

  },
  methods: {
    loadServerFilters: function() {
      this.loadingFilters = true;
      this.$apollo
        .query({
          query: LOAD_FILTERS_QUERY,
          fetchPolicy: "no-cache"
        })
        .then(response => {
          this.filtersList = response.data;
        })
        .catch(response => console.log(response))
        .finally(() => (this.loadingFilters = false));
    },
    loadServerEntries: function(firstLoading=false) {
      if (this.itemIds.length < 1) {
        
        return;
      }
      this.loading = true;
      this.$apollo
        .query({
          query: LOAD_STORAGE_MOVEMENTS_REPORT,
          variables: {
            storageIds: this.storageIds,
            itemIds: this.itemIds,
            packagingIds: this.packagingIds,
            startDate: this.startDate,
            endDate: this.endDate
          },
          fetchPolicy: "no-cache"
        })
        .then(response => {
          this.storageMovementsReport = response.data.accountContext.storageMovementsReport;
          // console.log("this.storageMovementsReport", this.storageMovementsReport);
        })
        .catch(response => console.log(response))
        .finally(() => (this.loading = false));
    },
    itemClasses: function(item) {
      if (item.movementType == "entry") {
        return "storage_movement_entry_item";
      } else if (item.movementType == "exit") {
        return "storage_movement_exit_item";
      }
    },
    runFilter: function(firstLoading=false) {
      if (this.periodFilterModel == null) {
        this.startDate = null;
        this.endDate   = null;
      } else if (this.periodFilterModel != "OTHER") {
        this.startDate = moment().startOf(this.periodFilterModel.toLocaleLowerCase()).format('YYYY-MM-DD');
        this.endDate   = moment().endOf(this.periodFilterModel.toLocaleLowerCase()).format('YYYY-MM-DD');

      } else {
        if (this.periodFilterRangeModel.length == 2) {
          this.startDate = this.periodFilterRangeModel[0];
          this.endDate   = this.periodFilterRangeModel[1];
        }
      }
      // console.log(this.startDate, this.endDate);
      this.loadServerEntries(firstLoading);
      // this.startDate = moment(this.monthFilter, 'YYYY-MM').startOf('week').format('YYYY-MM-DD');
      // this.endDate   = moment(this.monthFilter, 'YYYY-MM').endOf('week').format('YYYY-MM-DD');
    },
  }
};

const LOAD_FILTERS_QUERY = gql `
  {
    accountContext {
      allStorages {
        id
        name
      }
      allItemsFromAccountStorages {
        id
        name
      }
      allPackagingsFromAccountStorages {
        id
        name
      }
    }
  }
`;

const LOAD_STORAGE_MOVEMENTS_REPORT = gql `
query ($startDate: ISO8601Date, $endDate: ISO8601Date, $storageIds: [ID!]!, $itemIds: [ID!]!, $packagingIds: [ID!]!) {
  accountContext {
    storageMovementsReport(storageIds: $storageIds, itemIds: $itemIds, packagingIds: $packagingIds, startDate: $startDate, endDate: $endDate) 
  }
}
`;

</script>

<style scoped>
  ::v-deep .storage_movement_entry_item {
    color: blue;
  }
  ::v-deep .storage_movement_exit_item {
    color: red;
  }
</style>